import type {
  DragItem,
  DropPosition,
  DropTarget,
  Key
} from '@react-types/shared';
import {
  type ClipboardEvent,
  type CSSProperties,
  Fragment,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  DragAndDropContext,
  DropIndicator,
  type DropItem,
  GridList,
  type Selection,
  useDragAndDrop
} from 'react-aria-components';
import {
  CollectionDropIndicatorSvg,
  CollectionDropIndicatorVerticalSvg
} from 'v5/patterns/dnd/CollectionDropIndicator';
import { DragStack } from 'v5/patterns/dnd/DragStack';
import {
  areEqualKeys,
  moveKeysAfter,
  moveKeysBefore
} from 'v5/patterns/dnd/collectionReorder';
import { SelectionLayout } from 'v5/patterns/page-layout/SelectionLayout';
import { cn } from 'v5/platform/dom/cn';
import { useDndPresenceGuard } from './useDndPresenceGuard';
import type { ItemsInRect, TileLayout } from './tileLayouts';

/** What the collection tells the tile (`TileShell`, with `ImmersiveCard` or `DataCard` as its body). */
export interface TileState {
  key: string;
  textValue: string;
  /** The tile's box in the layout. */
  style: CSSProperties | undefined;
  selectable: boolean;
  /** Some tile is picked: a click picks too, and the cards show it (circles stay, the rest dim). */
  picking: boolean;
  /** Picked with a marquee too: the tile names itself to it (`data-item-id`). */
  marquee: boolean;
  /** It drags (to move, or out): it shows its handle, the keyboard's way to drag. */
  draggable: boolean;
  /** The collection's order: when it changes, a tile that animates its moves slides to its place. */
  arrangement: string;
  /** Where a drag over the collection would land, at the tile's edge; none where the layout draws it. */
  dropLine: ReactNode;
  /** A press or Enter on the tile. */
  onAction: (() => void) | undefined;
}

/** The host keeps which tiles are picked; `marquee` adds the rectangle from the empty space. */
export interface TileSelection {
  selected: ReadonlySet<string>;
  onChange(selected: ReadonlySet<string>): void;
  marquee?: boolean;
  /** ⌘A picks every tile; off for a list read page by page, where not every one is there. */
  selectAll?: boolean;
  /** ⌘A picks every one, also those not shown (a paged list's "all of them"). */
  onSelectAll?(): void;
}

/** The tile a drop lands beside, as React Aria saw it; `null` on the empty space around the tiles. */
export type TileBeside = { key: string; position: DropPosition } | null;

/** The order is the user's: tiles move by drag and from the keyboard. */
export interface TileReorder<T> {
  /** The items' new order, and the move that made it (for a host that saves moves, not orders). */
  onReorder(keys: string[], move: { keys: string[]; beside: TileBeside }): void;
  /** One layer of the drag preview's stack. */
  dragLayer(item: T): ReactNode;
}

/** Tiles drag out to another place, carrying what the host gives them; with `reorder`, they move here too. */
export interface TileOutgoing<T> {
  /** What one tile carries out (the host's own drag types); `null`: it stays, no handle and no drag. */
  itemOf(item: T): DragItem | null;
  /** One layer of the drag preview's stack; the reorder's without it. */
  dragLayer?(item: T): ReactNode;
}

/** A plug-in for what comes in from outside: drops and pastes. */
export interface TileIncoming {
  /** The drag types it takes; any without it. */
  types?: string[];
  /** `at`: among the items, `null` for the end; `beside`: for a host that saves places by neighbour. */
  onDrop(items: DropItem[], at: number | null, beside: TileBeside): void;
  onPaste?(event: ClipboardEvent<HTMLElement>): void;
}

/** Tiles after the items that never move: the host's own (things on their way in, an Add tile). */
export interface TrailingTiles {
  /** In order; React Aria may pick them (what was just dropped), but they never drag. */
  keys: string[];
  /** Take focus and their action, never a place among the picks. */
  unpickable?: string[];
  /** `dropLineAt(index)`: the line at the trailing tile `index` (from 0). */
  render(dropLineAt: (index: number) => ReactNode): ReactNode;
}

export interface TileCollectionProps<T> {
  label: string;
  items: T[];
  keyOf(item: T): string;
  /** What a dragged tile carries as text. */
  textOf(item: T): string;
  layout: TileLayout;
  /** The host's tile: `TileShell` around its body (`DataCard`, its own), or `ImmersiveCard`. */
  renderTile(item: T, tile: TileState): ReactElement;
  /** Absent: nothing to pick. */
  selection?: TileSelection;
  /** A tile not read yet: never picked, the marquee passes it by. */
  pickable?(item: T): boolean;
  /** Absent: the order is fixed. */
  reorder?: TileReorder<T>;
  outgoing?: TileOutgoing<T>;
  incoming?: TileIncoming;
  /** Off: the order isn't the user's (a sorted list); a drop anywhere lands at the end. */
  dropsBetween?: boolean;
  /**
   * For now nothing is picked, moved or dropped (a preview); the list stays as it is, scrolled
   * where it was.
   */
  readOnly?: boolean;
  onOpen?(key: string): void;
  /** Delete or Backspace with tiles picked. */
  onRemove?(keys: string[]): void;
  trailing?: TrailingTiles;
  /** Shown in place of the list while there are no tiles. */
  empty?: ReactNode;
  /**
   * Shown in the list while there are no tiles, instead of `empty`: the list stays, so what comes
   * in (`incoming`) still drops onto it, lit while it would land.
   */
  emptyState?: ReactNode;
  className?: string;
  'data-testid'?: string;
  /** After the list: the host's bar, its viewer. */
  children?: ReactNode;
}

const NONE_SELECTED: ReadonlySet<string> = new Set();

/** The drag type carrying a tile's key while the collection's own tiles move. */
const TILE_KEY_TYPE = 'application/x-tile-collection-key';

/**
 * Where among the items a drop at `target` lands; `keys.length` for the end, where the trailing
 * tiles stand.
 */
// a check or a button is an input too, but nothing is typed into it (a tile's circle)
const NOT_TYPED_INTO = [
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit'
];

const isTextField = (target: EventTarget) =>
  (target instanceof HTMLInputElement &&
    !NOT_TYPED_INTO.includes(target.type)) ||
  target instanceof HTMLTextAreaElement ||
  (target instanceof HTMLElement && target.isContentEditable);

function insertionIndex(keys: string[], target: DropTarget): number {
  if (target.type !== 'item') return keys.length;
  const index = keys.indexOf(String(target.key));
  if (index === -1) return keys.length;
  return target.dropPosition === 'after' ? index + 1 : index;
}

const besideOf = (target: DropTarget): TileBeside =>
  target.type === 'item'
    ? { key: String(target.key), position: target.dropPosition }
    : null;

/** The items' order once `dragged` lands at `at` (`insertionIndex`). */
function reorderedKeys(
  keys: string[],
  dragged: Iterable<Key>,
  at: number
): string[] {
  const moving = [...dragged].map(String).filter(key => keys.includes(key));
  return at < keys.length
    ? moveKeysBefore(keys, keys[at], moving)
    : moveKeysAfter(keys, keys[keys.length - 1], moving);
}

/** Whether dropping what is dragged at `target` would leave the order as it is. */
function changesNothing(
  keys: string[],
  dragging: ReadonlySet<Key> | undefined,
  target: DropTarget
): boolean {
  return (
    dragging !== undefined &&
    dragging.size > 0 &&
    areEqualKeys(
      reorderedKeys(keys, dragging, insertionIndex(keys, target)),
      keys
    )
  );
}

/**
 * Tiles in a React Aria grid list, the mechanism the gallery-like screens share. The host gives
 * the layout, the card around each tile's body and the plug-ins; the collection keeps the rules:
 *
 * - Picking (the host keeps the picks): the card's circle or Space, Shift for a range, ⌘A, Esc;
 *   while any is picked a click picks too. With `marquee`, a drag across the gaps picks what it
 *   covers and a click between the tiles clears.
 * - Reordering: a drag moves every picked tile, a line in the gap shows where they land; from the
 *   keyboard from a tile's handle. The preview stacks the dragged tiles.
 * - Incoming: drops from outside land at the same line; pastes anywhere in it.
 * - Keyboard: arrows between tiles, Enter opens, Delete removes the picked ones.
 */
export function TileCollection<T>({
  label,
  items,
  keyOf,
  textOf,
  layout,
  renderTile,
  selection,
  pickable,
  reorder,
  outgoing,
  incoming,
  dropsBetween = true,
  readOnly = false,
  onOpen,
  onRemove,
  trailing,
  empty,
  emptyState,
  className,
  'data-testid': testId,
  children
}: TileCollectionProps<T>) {
  const keys = items.map(keyOf);
  const trailingKeys = trailing?.keys ?? [];
  const unpickable = [
    ...(pickable ? items.filter(item => !pickable(item)).map(keyOf) : []),
    ...(trailing?.unpickable ?? [])
  ];
  const tileKeys = [...keys, ...trailingKeys];
  const isEmpty =
    keys.length === 0 &&
    trailingKeys.every(key => trailing?.unpickable?.includes(key));
  const reorderable = reorder !== undefined;
  useDndPresenceGuard(label, {
    reorder: reorder !== undefined,
    outgoing: outgoing !== undefined,
    incoming: incoming !== undefined
  });
  const draggable = reorderable || outgoing !== undefined;
  const itemByKey = new Map(items.map(item => [keyOf(item), item]));
  // without a reorder, a tile with nothing to carry out doesn't drag
  const stays = (key: string) => {
    const item = itemByKey.get(key);
    return (
      !reorderable &&
      (item === undefined || (outgoing?.itemOf(item) ?? null) === null)
    );
  };
  const selected = selection?.selected ?? NONE_SELECTED;
  const picking = !readOnly && selected.size > 0;
  // React Aria's own picks carry where a Shift range starts; handed back while the host keeps
  // the same keys, the range grows from there
  const lastPicks = useRef<Selection | null>(null);
  const last = lastPicks.current;
  const keptPicks =
    last !== null &&
    last !== 'all' &&
    last.size === selected.size &&
    [...selected].every(key => last.has(key))
      ? last
      : null;
  // the same picks as the same array: a new one makes React Aria re-render every tile it draws
  const pickedKeys = useRef<string[]>([]);
  if (
    pickedKeys.current.length !== selected.size ||
    pickedKeys.current.some(key => !selected.has(key))
  ) {
    pickedKeys.current = [...selected];
  }
  // a press with Shift picks the range from the last pick, from the circle too (React Aria's
  // circle flips only its own); the keyboard's ranges stay React Aria's
  const anchorRef = useRef<string | null>(null);
  const shiftPressRef = useRef<string | null>(null);
  // a layout that draws only what is on screen says where the rest stand
  const [itemsInRect, setItemsInRect] = useState<ItemsInRect | null>(null);
  const answersItemsInRect = useCallback(
    (answer: ItemsInRect | null) => setItemsInRect(() => answer),
    []
  );
  // keys typed in a tile's text field are the field's: React Aria's type-to-find hears them first
  const [typing, setTyping] = useState(false);
  const wrapped = (list: ReactElement) =>
    layout.wrap ? layout.wrap(list, answersItemsInRect) : list;

  const { dragAndDropHooks } = useDragAndDrop({
    // off, not left out: the list calls the drag hooks only when it gets them, so hooks coming and
    // going with a preview would change its hook order
    isDisabled: readOnly,
    getItems: draggable
      ? dragged =>
          [...dragged].map(String).flatMap(key => {
            const item = itemByKey.get(key);
            const carried = item && outgoing ? outgoing.itemOf(item) : null;
            if (!reorderable && carried === null) return [];
            return [
              {
                'text/plain': item ? textOf(item) : key,
                ...carried,
                [TILE_KEY_TYPE]: key
              }
            ];
          })
      : undefined,
    // moved here, copied out
    getAllowedDropOperations: () =>
      reorderable && outgoing
        ? ['move', 'copy']
        : reorderable
          ? ['move']
          : ['copy'],
    acceptedDragTypes: incoming?.types
      ? [TILE_KEY_TYPE, ...incoming.types]
      : undefined,
    getDropOperation: dropsBetween
      ? undefined
      : (target, _types, allowed) =>
          target.type === 'item' ? 'cancel' : allowed[0],
    renderDragPreview: dragItems => {
      const dragged = dragItems.flatMap(dragItem =>
        items.filter(item => keyOf(item) === dragItem[TILE_KEY_TYPE])
      );
      return (
        <DragStack
          count={dragged.length}
          layers={dragged.map(item => (
            <Fragment key={keyOf(item)}>
              {outgoing?.dragLayer?.(item) ?? reorder?.dragLayer(item)}
            </Fragment>
          ))}
        />
      );
    },
    renderDropIndicator: target =>
      layout.dropLine ? (
        <BetweenDropLine target={target} keys={keys} axis={layout.dropLine} />
      ) : (
        // There for keyboard and screen reader drags; the tiles draw the line.
        <DropIndicator
          target={target}
          className="absolute size-0 overflow-hidden"
        />
      ),
    onReorder: reorder
      ? ({ keys: dragged, target }) => {
          if (target.dropPosition === 'on') return;
          const next = reorderedKeys(
            keys,
            dragged,
            insertionIndex(keys, target)
          );
          if (areEqualKeys(next, keys)) return;
          reorder.onReorder(next, {
            keys: [...dragged].map(String),
            beside: besideOf(target)
          });
        }
      : undefined,
    onInsert: incoming
      ? ({ items: dropped, target }) => {
          const at = insertionIndex(keys, target);
          incoming.onDrop(
            dropped,
            at < keys.length ? at : null,
            besideOf(target)
          );
        }
      : undefined,
    onRootDrop: incoming
      ? ({ items: dropped }) => incoming.onDrop(dropped, null, null)
      : undefined
  });

  // Tiles slide only when this changes, never when the host moves the collection in the same
  // render.
  const arrangement = tileKeys.join('|');
  const tileCount = tileKeys.length;
  const latestOnOpen = useRef(onOpen);
  latestOnOpen.current = onOpen;
  // React Aria's "all", and Cmd+A after a press between the tiles
  const pickAll = () => {
    if (!selection || selection.selectAll === false) return;
    if (selection.onSelectAll) {
      selection.onSelectAll();
      return;
    }
    selection.onChange(new Set(keys.filter(key => !unpickable.includes(key))));
  };
  const latestPickAll = useRef(pickAll);
  latestPickAll.current = pickAll;
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let byPointer = false;
    // the tile the focus is on, as a click left it there
    const focusedTile = () => {
      const focused = document.activeElement;
      return focused instanceof HTMLElement &&
        !isTextField(focused) &&
        rootRef.current?.contains(focused) &&
        focused.closest('[role="row"]')
        ? focused
        : null;
    };
    // a press anywhere but on the focused tile lets it go: between the tiles React Aria keeps the
    // focus off its list, and page chrome that takes no focus would leave it on the tile, ringed
    // at the next key
    const onPointerDown = (event: PointerEvent) => {
      byPointer = true;
      const tile = focusedTile();
      const target = event.target;
      if (
        tile &&
        !(
          target instanceof Node &&
          tile.closest('[role="row"]')?.contains(target)
        )
      ) {
        tile.blur();
      }
    };
    // Escape after a pointer lets the clicked tile go, or its ring would light up for a key the user
    // never moved the focus with; from the keyboard the focus and its ring stay
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && byPointer) focusedTile()?.blur();
      byPointer = false;
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);
  const picks = selection !== undefined && !readOnly;
  useEffect(() => {
    if (!picks) return;
    let pressedInside = false;
    const notePress = (event: PointerEvent) => {
      pressedInside =
        event.target instanceof Node &&
        !!rootRef.current?.contains(event.target);
    };
    // React Aria keeps the focus off its list after a press between the tiles: the page has it,
    // and Cmd+A would select the page's text
    const pickAllKeys = (event: KeyboardEvent) => {
      const command = /Mac/.test(navigator.platform)
        ? event.metaKey
        : event.ctrlKey;
      if (
        !pressedInside ||
        !command ||
        event.key.toLowerCase() !== 'a' ||
        event.shiftKey ||
        event.altKey ||
        (document.activeElement !== null &&
          document.activeElement !== document.body)
      ) {
        return;
      }
      event.preventDefault();
      latestPickAll.current();
    };
    window.addEventListener('pointerdown', notePress, true);
    document.addEventListener('keydown', pickAllKeys);
    return () => {
      window.removeEventListener('pointerdown', notePress, true);
      document.removeEventListener('keydown', pickAllKeys);
    };
  }, [picks]);
  // Each tile's state stays the same object while nothing in it changes: a host's memoised tile
  // (`memo`) then skips the render when only another tile changed (a pick, a page read in).
  const tileStates = useRef(
    new Map<string, { signature: string; tile: TileState }>()
  );
  const keptStates = tileStates.current;
  tileStates.current = new Map();
  const dropLineAt = (index: number) =>
    layout.dropLine ? null : (
      <DropLine
        keys={keys}
        index={index}
        last={index === tileCount - 1}
        across={layout.arrows === 'stack'}
      />
    );

  const list = (
    <GridList
      // React Aria calls a different set of hooks once the list can drag (a second tile) or take
      // drops: a new list, not a changed one.
      key={`${reorderable}:${outgoing !== undefined}:${incoming !== undefined}`}
      aria-label={label}
      disallowTypeAhead={typing}
      layout={layout.arrows ?? 'grid'}
      selectionMode={selection && !readOnly ? 'multiple' : 'none'}
      selectedKeys={keptPicks ?? pickedKeys.current}
      onSelectionChange={next => {
        lastPicks.current = next;
        const pressed = shiftPressRef.current;
        shiftPressRef.current = null;
        const anchor = anchorRef.current;
        if (
          selection &&
          pressed !== null &&
          // the change is the press's, not a later one after a press that drew a rectangle
          next !== 'all' &&
          next.has(pressed) !== selected.has(pressed) &&
          anchor !== null &&
          keys.includes(anchor)
        ) {
          const ends = [keys.indexOf(anchor), keys.indexOf(pressed)];
          const range = keys.slice(Math.min(...ends), Math.max(...ends) + 1);
          selection.onChange(
            new Set(
              [...selected, ...range].filter(key => !unpickable.includes(key))
            )
          );
          return;
        }
        if (next !== 'all' && 'anchorKey' in next) {
          const { anchorKey } = next;
          if (typeof anchorKey === 'string' || typeof anchorKey === 'number') {
            anchorRef.current = String(anchorKey);
          }
        }
        if (next === 'all') {
          pickAll();
          return;
        }
        selection?.onChange(
          new Set(
            [...next].map(String).filter(key => !unpickable.includes(key))
          )
        );
      }}
      disabledKeys={unpickable}
      disabledBehavior="selection"
      renderEmptyState={emptyState === undefined ? undefined : () => emptyState}
      dragAndDropHooks={
        draggable || incoming !== undefined ? dragAndDropHooks : undefined
      }
      className={cn(
        'outline-none',
        // a drop on the area, not beside a tile (that draws its line): the whole list lights up,
        // drawn inside its edge so nothing moves
        'data-drop-target:rounded-xl data-drop-target:bg-brand-50 data-drop-target:outline-2 data-drop-target:-outline-offset-2 data-drop-target:outline-brand-500 data-drop-target:outline-solid',
        layout.className
      )}
      style={layout.style}
    >
      {items.map((item, index) => {
        const key = keyOf(item);
        const state = {
          textValue: textOf(item),
          selectable: selection !== undefined && !readOnly,
          picking,
          marquee: selection?.marquee === true && !unpickable.includes(key),
          draggable: draggable && !readOnly && !stays(key)
        };
        // the drop line follows the order (`arrangement`), its place in it and the layout's axis
        const signature = JSON.stringify([
          state,
          layout.tileStyle,
          arrangement,
          index,
          layout.dropLine ?? layout.arrows ?? null,
          onOpen !== undefined
        ]);
        const kept = keptStates.get(key);
        const tile =
          kept?.signature === signature
            ? kept.tile
            : {
                key,
                ...state,
                style: layout.tileStyle,
                arrangement,
                dropLine: dropLineAt(index),
                onAction: onOpen && (() => latestOnOpen.current?.(key))
              };
        tileStates.current.set(key, { signature, tile });
        return <Fragment key={key}>{renderTile(item, tile)}</Fragment>;
      })}
      {trailing?.render(index => dropLineAt(keys.length + index))}
    </GridList>
  );

  return (
    <div
      ref={rootRef}
      // a picked tile's or row's circle takes the accent of the picked ring
      className={cn(
        '[--selection-picked:var(--color-bg-accent-emphasis)]',
        className
      )}
      data-testid={testId}
      onFocus={event => setTyping(isTextField(event.target))}
      onBlur={() => setTyping(false)}
      onPaste={incoming?.onPaste}
      // React Aria drags every tile of a draggable list; the trailing tiles and those with nothing
      // to carry out stay put, so their drag never reaches it.
      onPointerDownCapture={event => {
        const key =
          event.target instanceof Element
            ? (event.target.closest('[data-key]')?.getAttribute('data-key') ??
              null)
            : null;
        shiftPressRef.current =
          event.shiftKey && key !== null && keys.includes(key) ? key : null;
      }}
      onDragStartCapture={event => {
        const tile =
          event.target instanceof Element
            ? event.target.closest('[data-key]')
            : null;
        const key = tile?.getAttribute('data-key') ?? null;
        if (key !== null && (trailingKeys.includes(key) || stays(key))) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      // while picking, React Aria's Enter picks the tile too; it opens it, as with nothing picked
      onKeyDownCapture={event => {
        if (event.key !== 'Enter' || !picking || !onOpen) return;
        const key =
          event.target instanceof Element && event.target.role === 'row'
            ? event.target.getAttribute('data-key')
            : null;
        if (key === null || !keys.includes(key)) return;
        event.preventDefault();
        event.stopPropagation();
        onOpen(key);
      }}
      onKeyDown={event => {
        if (isTextField(event.target)) return;
        if (onRemove && (event.key === 'Delete' || event.key === 'Backspace')) {
          const doomed = keys.filter(key => selected.has(key));
          if (doomed.length === 0) return;
          event.preventDefault();
          onRemove(doomed);
        }
      }}
    >
      {isEmpty && empty !== undefined ? (
        empty
      ) : selection?.marquee ? (
        <SelectionLayout
          enabled={!readOnly}
          // a drag from a tile drags it where tiles drag (here or out); elsewhere it draws the
          // rectangle too
          itemsDrag={draggable}
          ignoresHandledEscape
          collectionRootIsShell
          selectedIds={[...selection.selected]}
          onSelectedIdsChange={ids => selection.onChange(new Set(ids))}
          itemsInRect={
            itemsInRect
              ? rect =>
                  itemsInRect(rect).filter(
                    key => itemByKey.has(key) && !unpickable.includes(key)
                  )
              : undefined
          }
        >
          <div
            className={
              cn(
                layout.fills && 'flex min-h-0 flex-1 flex-col',
                layout.grows && 'flex flex-1 flex-col'
              ) || undefined
            }
          >
            {wrapped(list)}
          </div>
        </SelectionLayout>
      ) : (
        wrapped(list)
      )}
      {children}
    </div>
  );
}

/**
 * The line in the gap before tile `index` (the last tile: also after it) where a drag over the
 * collection would land, unless it changes nothing there; `across` a stack, above and below.
 */
function DropLine({
  keys,
  index,
  last,
  across
}: {
  keys: string[];
  index: number;
  last: boolean;
  across: boolean;
}) {
  const { dragState, dropState } = useContext(DragAndDropContext);
  const target = dropState?.target;
  // a drop on the area lights the whole list instead
  if (
    !target ||
    target.type === 'root' ||
    changesNothing(keys, dragState?.draggingKeys, target)
  ) {
    return null;
  }
  const at = insertionIndex(keys, target);
  const edge =
    at === index ? 'before' : last && at === index + 1 ? 'after' : null;
  if (edge === null) return null;
  if (across) {
    return (
      <span
        aria-hidden
        className={cn(
          // As tall as the drawn line, so its middle is the gap's middle.
          'pointer-events-none absolute inset-x-0 z-10 h-[10px]',
          edge === 'before'
            ? 'top-[calc(var(--tile-collection-gap)/-2)] -translate-y-1/2'
            : 'bottom-[calc(var(--tile-collection-gap)/-2)] translate-y-1/2'
        )}
      >
        <CollectionDropIndicatorSvg />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        // As wide as the drawn line, so its middle is the gap's middle.
        'pointer-events-none absolute inset-y-0 z-10 w-[10px]',
        edge === 'before'
          ? 'left-[calc(var(--tile-collection-gap)/-2)] -translate-x-1/2'
          : 'right-[calc(var(--tile-collection-gap)/-2)] translate-x-1/2'
      )}
    >
      <CollectionDropIndicatorVerticalSvg />
    </span>
  );
}

/**
 * The line drawn in the box the layout gives a drop place (a virtualizer's); none where dropping
 * what is dragged changes nothing.
 */
function BetweenDropLine({
  target,
  keys,
  axis
}: {
  target: DropTarget;
  keys: string[];
  axis: 'vertical' | 'horizontal';
}) {
  const { dragState } = useContext(DragAndDropContext);
  const still =
    target.type === 'item' &&
    target.dropPosition !== 'on' &&
    changesNothing(keys, dragState?.draggingKeys, target);
  return (
    <DropIndicator
      target={target}
      className={cn(
        'relative size-full overflow-visible opacity-0',
        !still && 'data-drop-target:opacity-100'
      )}
    >
      {axis === 'vertical' ? (
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2">
          <CollectionDropIndicatorVerticalSvg />
        </div>
      ) : (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2">
          <CollectionDropIndicatorSvg />
        </div>
      )}
    </DropIndicator>
  );
}
